import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const SavedContext = createContext(null);

export function SavedProvider({ children }) {
  const { user, token } = useAuth();
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch saved IDs when user logs in
  useEffect(() => {
    if (!user || !token) { setSavedIds(new Set()); return; }
    setLoading(true);
    fetch(`${API}/api/saved/ids`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(items => setSavedIds(new Set(items.map(i => `${i.type}:${i.item_id}`))))
      .catch(() => setSavedIds(new Set()))
      .finally(() => setLoading(false));
  }, [user, token]);

  const isSaved = useCallback((itemId, type = 'article') => {
    return savedIds.has(`${type}:${itemId}`);
  }, [savedIds]);

  const toggleSaved = useCallback(async (item) => {
    if (!user || !token) return { needsAuth: true };
    const key = `${item.type || 'article'}:${item.item_id}`;
    // Optimistic update
    setSavedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    try {
      const res = await fetch(`${API}/api/saved/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(item),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (e) {
      // Revert on error
      setSavedIds(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
      return { error: e.message };
    }
  }, [user, token]);

  return (
    <SavedContext.Provider value={{ savedIds, isSaved, toggleSaved, loading }}>
      {children}
    </SavedContext.Provider>
  );
}

export const useSaved = () => useContext(SavedContext);
